import { TableTd } from '../models/input/table-td';
import { ValueCell } from '../models/input/value-cell';
import { TableTh } from '../models/input/table-th';
import * as moment from 'moment';
import { IDatePickerConfig } from 'ng2-date-picker';
import { TranslateEnumService } from 'src/app/services/translate-enum.service';
import { SelectModel } from '../../select/models/select-model';
import { DefaultBooleanChoiceEnum } from '../../select/enums/default-boolean-choice.enum';
import {TableModel} from '../models/input/table-model';
import {IconType} from '../../icon/enum/icon-type';

export class BaseTableMapper {

  sortedByColumnId: string;
  sortDirection: 'ASC' | 'DESC';
  table: TableModel;

  protected translateEnumService: TranslateEnumService = new TranslateEnumService();

  constructor() {}

  // ------------------------------------------------------------------------------
  //                              TD/TH CREATION
  // ------------------------------------------------------------------------------
  createTd(columnId: string): TableTd {
    return new TableTd(columnId);
  }

  createTh(columnId: string): TableTh {
    return new TableTh(columnId);
  }

  // ------------------------------------------------------------------------------
  //                      TD/TH CREATION WITH ONE VALUE CELL
  // ------------------------------------------------------------------------------
  createSimpleTd(columnId: string, displayName: string | number, dataType?: TableTd['dataType']): TableTd {
    const td = this.createTd(columnId);
    if (dataType) {
      td.dataType = dataType;
    }
    const vc = this.createSimpleCell(displayName);
    td.addVc(vc);
    return td;
  }

  createSimpleTh(columnId: string, displayName: string | number, dataId: string = null): TableTh {
    const th = this.createTh(columnId);
    const vc = this.createSimpleCell(displayName, dataId);
    th.addVc(vc);
    return th;
  }

  createSimpleTdDate(columnId: string, stringDate: string, dateFormat?: string, datePickerConfig?: IDatePickerConfig): TableTd {
    const td = this.createTd(columnId);
    td.dataType = 'DATE';
    const vc = this.createVCDate(stringDate);
    if (dateFormat) {
      vc.dateFormat = dateFormat;
    }
    if (datePickerConfig) {
      vc.datePickerConfig = datePickerConfig;
    }
    td.addVc(vc);

    return td;
  }

  createSimpleTdChips(columnId: string, displayName: string, icon: string | null, css: string): TableTd {
    const td = this.createTd(columnId);
    td.dataType = 'STRING';
    const vc = this.createVCChips(displayName, icon, css);
    td.addVc(vc);

    return td;
  }

  createSimpleTdIcon(columnId: string, icon: string, css: string): TableTd {
    const td = this.createTd(columnId);
    td.dataType = 'STRING';
    const vc = this.createVCIcon(icon, css);
    td.addVc(vc);

    return td;
  }

  createSimpleTdFile(displayName: string, columnId: string = null, dataId = null, attrAccept: string = '', css = ''): TableTd {
    const td = new TableTd();
    td.columnId = columnId;
    const cell = this.createVCFile(displayName, dataId, attrAccept, css);
    td.valueCells.push(cell);
    return td;
  }

  createSimpleThButton(columnId: string = null, displayName: string, dataId = null, css = ''): TableTh {
    return this.createSimpleTdButton(columnId, displayName, dataId, css, false) as TableTh;
  }

  createSimpleTdButton(columnId: string = null, displayName: string, dataId: string = null, css = '', isDisabled: boolean): TableTd {
    const td = new TableTd();
    td.columnId = columnId;
    const cell = this.createVCButton(displayName, dataId, css, isDisabled);

    td.valueCells.push(cell);
    return td;
  }

  createSimpleTdSplitButton(columnId: string = null, displayName: string, dataId: string = null, menuItems: { value: string, click: () => void }[]): TableTd {
    const td = new TableTd();
    td.columnId = columnId;
    const cell = this.createVCSplitButton(displayName, dataId, menuItems);

    td.valueCells.push(cell);
    return td;
  }

  createSimpleTdBoolean(columnId: string = null, displayName: boolean, dataId = null, css = ''): TableTd {
    const td = new TableTd();
    td.columnId = columnId;
    td.dataType = 'BOOLEAN';
    const cell = this.createVCBoolean(displayName, dataId, css);
    td.valueCells.push(cell);
    return td;
  }

  createSimpleTdCheckBox(columnId: string = null, check: boolean, disable: boolean, label: string): TableTd {
    const td = new TableTd();
    td.dataType = 'INPUT';
    td.columnId = columnId;
    const cell = this.createCheckBox(check, disable, label, '');
    td.valueCells.push(cell);
    return td;


  }

  // ------------------------------------------------------------------------------
  //                                VALUE CELL CREATION
  // ------------------------------------------------------------------------------
  createSimpleCell(displayName: string | number, dataId: string = null): ValueCell {
    const cell = new ValueCell();
    cell.displayedValue = displayName;
    cell.dataId = dataId;
    return cell;
  }

  createVCDate(displayName: string | number, dateFormat?: string): ValueCell {
    const vc = new ValueCell();
    vc.displayedValue = displayName;
    vc.value = moment.default(displayName);
    vc.dataType = 'DATE';
    if (dateFormat) {
      vc.dateFormat = dateFormat;
    }
    return vc;
  }

  createVCFile(displayName: string, dataId: string, attrAccept: string = '', css: string = ''): ValueCell {
    const vc = new ValueCell();
    vc.displayedValue = displayName;
    vc.tagType = 'file';
    vc.valueCssClass = css;
    vc.attrAccept = attrAccept;
    vc.dataId = dataId;
    return vc;
  }

  createVCButton(displayName: string, dataId: string, css: string = '', isDisabled): ValueCell {
    const vc = new ValueCell();
    vc.displayedValue = displayName;
    vc.tagType = 'button';
    vc.valueCssClass = css;
    vc.dataId = dataId;
    vc.isDisabled = isDisabled;
    return vc;
  }

  createVCSplitButton(displayName: string, dataId: string, menuItems: { value: string, click: () => void }[]): ValueCell {
    const vc = new ValueCell();
    vc.displayedValue = displayName;
    vc.tagType = 'splitButton';
    vc.dataId = dataId;
    vc.menuItems = menuItems;
    return vc;
  }

  createVCBoolean(displayName: boolean, dataId: string, css: string = ''): ValueCell {
    const vc = new ValueCell();
    vc.displayedValue = this.translateEnumService.getDisplayNameBoolean(displayName);
    vc.valueCssClass = css;
    vc.dataId = dataId;
    // generate select
    const choice: DefaultBooleanChoiceEnum = displayName ? DefaultBooleanChoiceEnum.TRUE : DefaultBooleanChoiceEnum.FALSE;
    const select: SelectModel = new SelectModel();
    select.generateSelectForBoolean(choice);
    vc.select = select;
    return vc;
  }

  createCheckBox(checked: boolean, disable: boolean, label: string, value: any): ValueCell {
    const vc = new ValueCell();
    vc.tagType = 'checkbox';
    vc.isChecked = checked;
    vc.isDisabled = disable;
    vc.displayedValue = label;
    vc.value = value;
    return vc;
  }

  createVCChips(displayName: string, icon: string | null, css: string): ValueCell {
    const vc = new ValueCell();
    vc.displayedValue = displayName;
    vc.tagType = 'chips';
    vc.valueCssClass = css;
    vc.icon = icon;
    return vc;
  }

  createVCIcon(icon: string, css: string): ValueCell {
    const vc = new ValueCell();
    vc.tagType = 'icon';
    vc.valueCssClass = css;
    vc.icon = icon;
    return vc;
  }

  // createSelect(checked: boolean, disable:boolean, label: string, value: any): ValueCell {
  //   const vc = new ValueCell();

  //   vc.htmlElementForEdition = 'SELECT'
  //   const select: SelectModel = new SelectModel();
  //   select.
  //   vc.isChecked = checked;
  //   vc.isDisabled = disable;
  //   vc.displayedValue = label;
  //   vc.value = value;
  //   return vc;
  // }



}
